import { useRouter } from 'next/router'
import { useState } from 'react'
import { motion } from 'framer-motion'
import Head from 'next/head'

let styles = {}
import stylesA from '@/styles/EVDetail.module.css'
import stylesB from '@/styles/index.module.css'

import Header from '@/components/Header'
import BreadCrumb from '@/components/ui/breadcrumb/BreadcrumbComponent'
import Footer from '@/components/home/v2/Footer'

import ImageDetails from '@/components/ev-details/ImageDetails'
import BasicDetails from '@/components/ev-details/BasicDetails'
import RangeDetails from '@/components/ev-details/RangeDetails'
import ChargingDetails from '@/components/ev-details/ChargingDetails'
import DimensionsDetails from '@/components/ev-details/DimensionsDetails'
import PerformanceDetails from '@/components/ev-details/PerformanceDetails'
import RangeInsights from '@/components/ev-details/v2/RangeInsights'
import ButtonComponent from '@/components/ui/buttons/ButtonComponent'

Object.assign(styles, stylesA, stylesB)

const EvDetail = ({ ev }) => {
  const router = useRouter()
  const { slug } = router.query
  const [showModal, setShowModal] = useState(false)

  const location = `${ev.make} ${ev.model} ${ev.year}`

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
      <Head>
        <title>{`ClearWatt | ${location} - Electric Vehicle Details`}</title>
        <meta
          name="description"
          content={`Explore detailed specifications, range, performance, and charging data for the ${ev.make} ${ev.model} (${ev.year}). Get a comprehensive overview with ClearWatt.`}
        />
        <meta name="viewport" content="maximum-scale=1, initial-scale=1, width=device-width" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://clearwatt.co.uk/directory/${slug}`} />
        <meta property="og:title" content={`${ev.make} ${ev.model} ${ev.year} - Electric Vehicle Details`} />
        <meta
          property="og:description"
          content={`Learn about the ${ev.make} ${ev.model} (${ev.year}): range, charging times, dimensions, and performance data. Ideal for EV buyers and enthusiasts.`}
        />
        <meta property="og:image" content={ev.image} />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={`https://clearwatt.co.uk/directory/${slug}`} />
        <meta property="twitter:title" content={`${ev.make} ${ev.model} ${ev.year} - Electric Vehicle Details`} />
        <meta
          property="twitter:description"
          content={`Get all the details about the ${ev.make} ${ev.model} (${ev.year}): range, performance, and charging specs. Powered by ClearWatt.`}
        />
        <meta property="twitter:image" content={ev.image} />

        {/* Simplified Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Product',
            name: `${ev.make} ${ev.model} ${ev.year}`,
            description: `Specifications and performance details for the ${ev.make} ${ev.model} (${ev.year}).`,
            image: ev.image,
            url: `https://clearwatt.co.uk/directory/${slug}`,
            brand: {
              '@type': 'Brand',
              name: ev.make,
            },
          })}
        </script>

        <link rel="icon" href="/favicon.png" />
      </Head>

      <Header />
      <div className={styles.details}>
        <div className={styles.detailsContainer}>
          <BreadCrumb location={location} />

          <h1 className={styles.H1}>
            {ev.make} {ev.model} {ev.year}
          </h1>

          <div className={styles.mainDetailsSection}>
            <ImageDetails images={ev.images} model={ev.model} make={ev.make} />
            <BasicDetails data={ev.basic_details} />
          </div>
          <div className={styles.detailsSection}>
            <RangeInsights insights={ev.benchmark_real_range_new} />
            <ChargingDetails data={ev.charge_details} />
          </div>
          <div className={styles.detailsSection}>
            <DimensionsDetails data={ev.dimensions_details} />
            <PerformanceDetails data={ev.performance_details} />
          </div>
          {/* Vehicle code section */}
          <div
            className={styles.internationalTester}
            onClick={() => setShowModal(true)}
            style={{ cursor: 'pointer' }} // indicate clickable
          >
            Need a vehicle code?
          </div>

          {/* Simple Modal */}
          {showModal && (
            <div className={styles.modalOverlay}>
              <div className={styles.modalContent}>
                <div className={styles.modalTitle}>Use the code below to add your non-UK vehicle within our app:</div>
                <p className={styles.vehicleCode}>{`REG-${ev.vehicle_id}`}</p>
                <div className={styles.modalFooter}>
                  Please ensure you complete the entire registration form and click submit at the end.
                </div>
                <div className={styles.buttonContainer}>
                  <ButtonComponent
                    onClick={() => setShowModal(false)}
                    variant={'secondary'}
                    text={'Close'}
                    className={styles.button}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </motion.div>
  )
}

export async function getServerSideProps(context) {
  const { slug } = context.params

  try {
    const apiUrl =
      process.env.NODE_ENV === 'development'
        ? `http://django:8000/api/v1/vehicles/vehicle_website_data/?slug=${slug}`
        : `${process.env.NEXT_PUBLIC_CLEARWATT_BASE_API}/vehicles/vehicle_website_data/?slug=${slug}`

    const response = await fetch(apiUrl)

    if (!response.ok) {
      throw new Error('Error obteniendo datos')
    }

    const data = await response.json()
    return {
      props: { ev: data },
    }
  } catch (error) {
    return {
      notFound: true,
    }
  }
}

export default EvDetail
